import {
  DATA_TYPES,
  ENTITY_RIGHT_DEFAULTS,
  INPUTS,
  ALLOW_NULL,
} from 'src/app/slicedForm/FilterForm/definitions/inputEnums';
import {
  formatLargePrice,
  formatPrice,
  formatLargeInteger,
  formatPercentage
} from 'src/utils/formatters';
import { GROUPS } from './columnDefs';

/** @typedef {import('./columnDefs').FilterColumnDef} FilterColumnDef */
/** @typedef {import('src/app/slicedForm/mapping/mappingDirections/index').ProfileFilterNode} ProfileFilterNode */


// Helps build the column definitions for the grid

const validate = (builtParams, groups) => {
  const { name, label, group, dtype } = builtParams;
  if (!name) {
    throw new Error('"name" field is required');
  }
  if (!label && label !== '') {
    throw new Error('"label" field is required. Can be empty string');
  }
  if (!group) {
    throw new Error('"group" field is required');
  }
  // if (!dtype) {
  //   throw new Error('"dtype" field is required');
  // }
  // if (!(dtype in DATA_TYPES)) {
  //   throw new Error('Invalid dtype. Must be one of: ' + Object.keys(DATA_TYPES).join(', '));
  // }

  if (Array.isArray(group)) {
    group.forEach(g => {
      if (!(g in groups)) {
        throw new Error(`Invalid array elemet group ${JSON.stringify(g)}. Must be one of: ${Object.keys(groups).join(', ')}`);
      }
    })
  } else if (!(group in groups)) {
    throw new Error(`Invalid group ${JSON.stringify(group)}. Must be one of: ${Object.keys(groups).join(', ')}`);
  }

  return builtParams;
};

const validateSelect = (builtParams) => {
  const { filter, dtype, name } = builtParams;
  if (!dtype) {
    throw new Error(`"dtype" field must explictly be declaired for "${name}" Select/MultiSelect column`);
  }
  if (!filter.options) {
    throw new Error('"filter.options" field is required must be a non-empty array');
  }
  if (!Array.isArray(filter.options)) {
    throw new Error('"filter.options" field is required must be a non-empty array');
  }
  if (!filter.options.length) {
    throw new Error('"filter.options" field is required must be a non-empty array');
  }
  filter.options.forEach(opt => {
    if (!opt.name || opt.label) {
      console.warn('Failing filter.options:', filter.options);
      throw new Error('"filter.options" must be an array of objects with "name" and "label" fields. See logs.');
    }
  });
  return builtParams;
};


// const validateDate = (builtParams) => {
//   const {

//   }
// }


/**
 * 
 * @param {ColumnDef[]} builtColDefs 
 * @returns {ColumnDef[]}
 * @throws {Error}
 */
export const validateColDefs = (builtColDefs, groups) => {
  builtColDefs.forEach(def => {
    validate(def, groups);
    if (def.filter.input in [INPUTS.MULTI_SELECT, INPUTS.SELECT]) {
      validateSelect(def);
    }
  });
  return builtColDefs;
};


/**
 * Defines:
 *  1) How the grid formats the value
 *  2) Which columns are valid comparisons to this column. (ah_vol > pm_vol)
 * 
 * This is entangling two systems (comparisons vs formats), but right now they map 1 to 1.
 */
export const COLUMN_TYPES = {
  largePrice: {
    name: 'largePrice',
    formatter: args => formatLargePrice(args.value),
    comparesTo: ['column_type.largePrice']
  },
  price: {
    name: 'price',
    formatter: args => formatPrice(args.value),
    comparesTo: ['column_type.price']
  },
  largeInteger: {
    name: 'largeInteger',
    formatter: args => formatLargeInteger(args.value),
    comparesTo: ['column_type.largeInteger']
  },
  percentage: {
    name: 'percentage',
    formatter: args => formatPercentage(args.value),
    comparesTo: ['column_type.percentage']
  },
  volume: {
    name: 'volume',
    formatter: args => formatLargeInteger(args.value),
    comparesTo: ['column_type.volume']
  },
  date: {
    name: 'date',
    formatter: null,
    comparesTo: ['column_type.date']
  },
  time: {
    name: 'time',
    formatter: null,
    comparesTo: ['column_type.time']
  },
}


/**
 * Helper methods to build common ColumnDef objects
 */
export const SCHEMA = {
  volume: ({ name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.VOLUME,
    column_type: COLUMN_TYPES.volume.name,
    ...shared,
    grid: {
      cellRenderer: 'compactNumberCellRenderer',
      sortable: true,
      valueFormatter: COLUMN_TYPES.volume.formatter,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      timeSlice: true,
      comparesTo: COLUMN_TYPES.volume.comparesTo,
      ...filter
    },
    column: { ...column }
  }),
  percentage: ({ change = false, name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.PERCENTAGE,
    column_type: COLUMN_TYPES.percentage.name,
    ...shared,
    grid: {
      cellRenderer: 'compactNumberCellRenderer',
      sortable: true,
      numberHighlighting: change,
      valueFormatter: COLUMN_TYPES.percentage.formatter,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      timeSlice: true,
      comparesTo: COLUMN_TYPES.percentage.comparesTo,
      ...filter
    },
    column: { ...column }
  }),
  price: ({ change = false, name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.PRICE,
    column_type: COLUMN_TYPES.price.name,
    ...shared,
    grid: {
      cellRenderer: 'compactNumberCellRenderer',
      sortable: true,
      numberHighlighting: change,
      valueFormatter: COLUMN_TYPES.price.formatter,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      timeSlice: true,
      comparesTo: COLUMN_TYPES.price.comparesTo,
      ...filter
    },
    column: { ...column }
  }),
  largePrice: ({ name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.LARGE_PRICE,
    column_type: COLUMN_TYPES.largePrice.name,
    ...shared,
    grid: {
      cellRenderer: 'compactNumberCellRenderer',
      sortable: true,
      valueFormatter: COLUMN_TYPES.largePrice.formatter,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      comparesTo: COLUMN_TYPES.largePrice.comparesTo,
      allowNull: ALLOW_NULL.USER_CONTROLLED,
      allowNullDefault: true,
      ...filter
    },
    column: { ...column }
  }),
  largeInteger: ({ name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.INT,
    column_type: COLUMN_TYPES.largeInteger.name,
    ...shared,
    grid: {
      cellRenderer: 'compactNumberCellRenderer',
      sortable: true,
      valueFormatter: COLUMN_TYPES.largeInteger.formatter,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      comparesTo: COLUMN_TYPES.largeInteger.comparesTo,
      allowNull: ALLOW_NULL.USER_CONTROLLED,
      allowNullDefault: true,
      ...filter
    },
    column: { ...column }
  }),
  multiSelect: ({ name, label, formLabel, group, options, dtype, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: dtype,
    ...shared,
    grid: {
      sortable: true,
      className: 'ag-right-aligned-cell ag-right-aligned-cell-padding',
      ...grid,
    },
    filter: {
      input: INPUTS.MULTI_SELECT,
      options,
      ...filter
    },
    column: { ...column }
  }),
  // Defaults will be set by ColumnProvider within the form code
  date: ({ name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {}, dateConfig = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.DATE,
    column_type: COLUMN_TYPES.date.name,
    ...shared,
    grid: {
      sortable: true,
      className: 'ag-right-aligned-cell ag-right-aligned-cell-padding',
      ...grid
    },
    filter: {
      input: INPUTS.DATE,
      timeSlice: false,
      column_type: COLUMN_TYPES.date.comparesTo,
      dateConfig,
      ...filter
    },
    column: { ...column }
  }),
  time: ({ name, label, formLabel, group, shared = {}, grid = {}, filter = {}, column = {}, dateConfig = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype: DATA_TYPES.TIME,
    column_type: COLUMN_TYPES.time.name,
    ...shared,
    grid: {
      sortable: true,
      className: 'ag-right-aligned-cell ag-right-aligned-cell-padding',
      ...grid
    },
    filter: {
      input: INPUTS.TIME,
      timeSlice: false,
      column_type: COLUMN_TYPES.time.comparesTo,
      dateConfig,
      ...filter
    },
    column: { ...column }
  }),
  // No formatters. Will not have any column comparisons. Integers or floats allowed.
  arbitraryNumber: ({ name, label, formLabel, group, dtype, shared = {}, grid = {}, filter = {}, column = {} }) => ({
    name,
    label,
    formLabel,
    group,
    dtype,
    ...shared,
    grid: {
      type: 'numericColumn',
      sortable: true,
      ...grid,
    },
    filter: {
      input: INPUTS.COMPARE,
      ...filter
    },
    column: { ...column }
  })
};



/**
 * Builds the default Form Entity value for a given metric, based on filter schema properties.
 * Right now this is thin, but it has the potential to:
 *  - Intialize default values per metric
 *  - Initialize extra property values like allowNull or timeSlice
 *  - Initialize with correct data structure like arrays, strings, etc.
 * @param {FilterColumnDef} filterColumnDef
 * @returns {ProfileFilterNode}
 */
export const getFilterEntityDefaults = (filterColumnDef) => {
  if (!filterColumnDef) throw new Error('filterColumnDef is required');

  const defaults = ENTITY_RIGHT_DEFAULTS[filterColumnDef.input];

  const schema = {
    left: {
      column: filterColumnDef.name,
      type: 'column'
    },
    ...defaults
  }

  if (filterColumnDef.allowNull === ALLOW_NULL.TRUE) {
    schema.left.allowNull = true;
  }
  if (filterColumnDef.allowNull === ALLOW_NULL.FALSE) {
    //
  }
  if (filterColumnDef.allowNull === ALLOW_NULL.USER_CONTROLLED) {
    if ('allowNullDefault' in filterColumnDef) {
      schema.left.allowNull = filterColumnDef.allowNullDefault;
    } else {
      schema.left.allowNull = true
    }
  }

  return schema;
}


export const SCANNER_FUSE_OPTIONS = {
  // ignoreLocation: true,
  threshold: 0.4,
  keys: [
    "label",
    {
      name: "synonyms",
      weight: 0.8
    },
    {
      name: "alternateLabel",
      weight: 0.6
    }
  ]
}


export const BASE_SYNONYMS = {
  'chg': ['change'],
  '%': ['percent'],
  'vol': ['volume'],
  'rvol': ['relative volume'],
  'o': ['open'],
  'h': ['high'],
  'l': ['low'],
  'c': ['close'],
  'own': ['ownership'],
  'inst.': ['institutional'],
  'ins': ['insider'],
  'pr': ['press'],
  'min': ['minute'],
  'ah': ['afterhours'],
  'pm': ['premarket'],
  'ssr': ['short sal restriction'],
  'vw': ['vwap'],
  'equiv': ['equivalent'],
  'qoq': ['quarter'],
  'eps': ['earnings per share'],
  'roa': ['return on assets'],
  'roe': ['return on equity'],
  'ret': ['return'],
  'close': ['prev close'],
  'd[-1]': ['day -1', 'prev'],
  'd[-2]': ['day -2'],
  'd[-3]': ['day -3'],
  'd[-4]': ['day -4'],
  'd[-5]': ['day -5'],
  'mo': ['month'],
  'ytd': ['year to date']
}


